import { useState } from 'react';
import { useSession } from 'next-auth/react';

import { showWarningToast, showSuccessToast } from '@/components/ui/sonner';
import { buildDirectBackendUrl } from '@/lib/direct-backend-api';
import { useProfileStore } from '@/store/profile.store';

const getAvatarUrl = (avatarUrl?: string) => {
  if (!avatarUrl) return '';
  if (avatarUrl.startsWith('http')) return avatarUrl;
  return avatarUrl;
};

export function useAvatar() {
  const [isLoading, setIsLoading] = useState(false);
  const { data: session, update: updateSession } = useSession();

  const uploadAvatar = async (file: File) => {
    if (!file) {
      showWarningToast('Файл не выбран');
      return false;
    }

    if (!file.type.startsWith('image/')) {
      showWarningToast('Файл должен быть изображением');
      return false;
    }

    const maxSize = 30 * 1024 * 1024;
    if (file.size > maxSize) {
      showWarningToast('Размер файла должен быть меньше 30MB');
      return false;
    }

    if (!session?.accessToken || session.user?.id == null) {
      showWarningToast('Требуется авторизация');
      return false;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('avatar', file);
      formData.append('userId', String(session.user.id));

      const response = await fetch(buildDirectBackendUrl('/api/users/avatar'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
        },
        body: formData,
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        if (updateSession) {
          await updateSession({
            ...session,
            user: {
              ...session?.user,
              id: session.user.id,
              name: session.user.name,
              email: session.user.email,
              image: getAvatarUrl(
                data.avatar?.url ||
                  data.avatar?.thumbnailURL ||
                  data.user?.avatar?.url ||
                  data.user?.avatar?.thumbnailURL,
              ),
            },
          });
        }
        return true;
      }
      showWarningToast((data as { error?: string }).error || 'Не удалось обновить аватар');
      return false;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      showWarningToast(error instanceof Error ? error.message : 'Не удалось обновить аватар');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAvatar = async () => {
    if (!session?.accessToken || session.user?.id == null) {
      showWarningToast('Требуется авторизация');
      return false;
    }

    setIsLoading(true);

    try {
      const response = await fetch(buildDirectBackendUrl('/api/users/avatar'), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
        },
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        const currentUserData = useProfileStore.getState().userData;
        if (currentUserData) {
          useProfileStore.setState({
            userData: {
              ...currentUserData,
              avatar: undefined,
            },
            lastFetchTime: null,
          });
        }

        if (updateSession) {
          await updateSession({
            ...session,
            user: {
              ...session?.user,
              id: session.user.id,
              name: session.user.name,
              email: session.user.email,
              image: '',
            },
          });
        }
        return true;
      }

      showWarningToast((data as { error?: string }).error || 'Не удалось удалить аватар');
      return false;
    } catch (error) {
      console.error('Error deleting avatar:', error);
      showWarningToast(error instanceof Error ? error.message : 'Не удалось удалить аватар');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const uploadBanner = async (file: File) => {
    if (!file) {
      showWarningToast('Файл не выбран');
      return false;
    }

    if (!file.type.startsWith('image/')) {
      showWarningToast('Файл должен быть изображением');
      return false;
    }

    const maxSize = 30 * 1024 * 1024;
    if (file.size > maxSize) {
      showWarningToast('Размер файла должен быть меньше 30MB');
      return false;
    }

    if (!session?.accessToken || session.user?.id == null) {
      showWarningToast('Требуется авторизация');
      return false;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('banner', file);
      formData.append('userId', String(session.user.id));

      const response = await fetch(buildDirectBackendUrl('/api/users/banner'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
        },
        body: formData,
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        showSuccessToast('Баннер обновлен');
        return true;
      }
      throw new Error((data as { message?: string }).message || 'Ошибка при загрузке баннера');
    } catch (error) {
      console.error('Error uploading banner:', error);
      showWarningToast(error instanceof Error ? error.message : 'Не удалось обновить баннер');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    uploadAvatar,
    deleteAvatar,
    uploadBanner,
    isLoading,
  };
}
